import { Navigate, Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { CheckCircle2, ArrowRight, MessageCircle } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import ServiceEnquiryForm from "@/components/booking/ServiceEnquiryForm";
import { SERVICE_ENQUIRY_PAGES } from "@/data/serviceEnquiryPages";
import { serviceSchema, breadcrumbSchema } from "@/lib/seo";
import { CTA } from "@/lib/business";
import { waHref } from "@/lib/contact";
import { trackEvent } from "@/lib/analytics";

/**
 * Dedicated service page with its own embedded enquiry form (defect cost
 * estimation / site inspection). Same branding, layout rhythm and schema
 * markup as the other service pages.
 */
export default function ServiceEnquiryPage({ slug }) {
  const data = SERVICE_ENQUIRY_PAGES[slug];
  const formRef = useRef(null);

  useEffect(() => {
    if (data) trackEvent("view_service", { service: slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!data) return <Navigate to="/404" replace />;

  const path = `/${slug}`;
  const breadcrumbs = [{ name: data.breadcrumb, path }];
  const jsonLd = [
    serviceSchema({ name: data.h1, description: data.description, serviceType: data.serviceName, path }),
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: data.breadcrumb, path }]),
  ];
  const scrollToForm = () => {
    trackEvent("click_primary_cta", { location: `service-page-${data.serviceKey}` });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell breadcrumbs={breadcrumbs}>
      {() => (
        <>
          <Seo title={data.title} description={data.description} path={path} jsonLd={jsonLd} />

          {/* Intro */}
          <section className="wrap pt-6 pb-12 lg:pt-10 lg:pb-16">
            <span className="eyebrow">Independent property inspection · UAE</span>
            <h1 className="text-[clamp(2rem,4vw,3rem)] leading-[1.06] tracking-[-0.03em] mt-3 mb-3 max-w-[20ch]">{data.h1}</h1>
            <p className="text-[1.15rem] text-ink font-medium max-w-[52ch] mb-4" data-testid="service-subtitle">{data.subtitle}</p>
            <p className="text-[1.05rem] text-bodytext max-w-[62ch] mb-6">{data.intro}</p>
            {data.note && (
              <p className="text-[0.84rem] text-muted max-w-[62ch] mb-6" data-testid="service-note-intro">
                <span className="font-semibold text-bodytext">Please note: </span>{data.note}
              </p>
            )}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button onClick={scrollToForm} data-testid="service-enquiry-cta"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                {data.cta} <ArrowRight size={17} />
              </button>
              <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-wa hover:text-wa transition-colors">
                <MessageCircle size={17} /> {CTA.whatsapp}
              </a>
            </div>
          </section>

          {/* What you receive */}
          <section className="wrap pb-14">
            <div className="max-w-[80ch]">
              <h2 className="text-xl mb-4">What you receive</h2>
              <ul className="space-y-2.5">
                {data.receive.map((it, i) => (
                  <li key={i} className="flex gap-2.5 text-bodytext">
                    <CheckCircle2 size={18} className="text-blue flex-none mt-0.5" aria-hidden="true" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Enquiry form */}
          <section ref={formRef} id="enquiry-form" className="wrap pb-14 scroll-mt-[130px] lg:scroll-mt-[190px]">
            <div className="bg-soft border border-line rounded-2xl p-6 lg:p-10 max-w-[860px]">
              <span className="eyebrow">{data.serviceName}</span>
              <h2 className="text-2xl mt-2 mb-2">{data.cta}</h2>
              <p className="text-muted text-[0.95rem] mb-6 max-w-[56ch]">Share the details below — Fedari reviews every enquiry personally and responds on your preferred channel.</p>
              <ServiceEnquiryForm config={data} />
              {data.note && (
                <p className="text-[0.8rem] text-muted mt-6 pt-5 border-t border-line" data-testid="service-note-form">
                  <span className="font-semibold text-bodytext">Please note: </span>{data.note}
                </p>
              )}
            </div>
          </section>

          {/* Related */}
          <section className="wrap pb-16">
            <h2 className="text-xl mb-4">Related services</h2>
            <div className="flex flex-wrap gap-3">
              {data.related.map((r) => (
                <Link key={r.href} to={r.href} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-line text-ink font-medium hover:border-blue hover:text-blue transition-colors">
                  {r.label} <ArrowRight size={15} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
